"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MapPin, MessageSquare, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqs = [{
  id: "faq-1",
  question: "Why is Local SEO crucial for Swansea businesses specifically?",
  answer: "Swansea has a dynamic business landscape across sectors like retail, tourism, hospitality, professional services, and marine industries. When local customers search for services near them or in specific areas like Uplands, Mumbles, or SA1 Waterfront, Google Map Pack and localized organic search results drive over 60% of high-intent clicks. I help you dominate these local search positions so you capture customers precisely when they are ready to buy."
}, {
  id: "faq-2",
  question: "How quickly can my Swansea business expect visible local SEO results?",
  answer: "For local Google Map Pack improvements and hyper-local search intent, most of my Swansea clients see initial momentum within 3 to 8 weeks after optimizing Google Business Profiles, fixing local citations, and tuning location landing pages. Competitive keywords across South Wales typically achieve strong top-tier rankings within 3 to 6 months of continuous strategy execution."
}, {
  id: "faq-3",
  question: "Will I work directly with Yassine or be handed off to an account manager?",
  answer: "You work directly with me—Yassine Essaadi—from day one. I handle your strategic audit, technical fixes, local content strategy, and ongoing reporting. You will never be passed off to a junior account manager, offshore team, or automated ticket system."
}, {
  id: "faq-4",
  question: "How do you optimize Google Business Profiles for Swansea locations?",
  answer: "I conduct a deep audit of your Google Business Profile (GBP), optimizing primary and secondary categories, geographic service radius, business descriptions, product/service listings, local geo-tagged assets, and review velocity strategies tailored specifically to Swansea and surrounding South Wales communities."
}, {
  id: "faq-5",
  question: "Can you help my Swansea business target nearby areas like Neath, Port Talbot, and Llanelli?",
  answer: "Yes. Local search expansion requires structured local landing pages, geographic schema markups, and localized backlink building targeting surrounding hubs along the M4 corridor, West Glamorgan, and Carmarthenshire without triggering Google spam filters."
}, {
  id: "faq-6",
  question: "What makes Local SEO different from traditional National SEO?",
  answer: "Local SEO focuses on high-intent proximity signals, localized search queries (e.g., 'plumber in Swansea' or 'best cafe near Mumbles'), Google Maps placement, NAP (Name, Address, Phone) consistency, local reviews, and regional backlinks. National SEO targets broader, non-geographic keywords across the entire UK."
}, {
  id: "faq-7",
  question: "How do you handle bilingual or Welsh language search intent in Swansea?",
  answer: "Where relevant for public sector, tourism, or regional audiences, I optimize site structure and hreflang/meta signals to capture Welsh language and bilingual search terms naturally, ensuring your brand connects seamlessly with local search intent."
}, {
  id: "faq-8",
  question: "What local Swansea directories and citations do you build and audit?",
  answer: "I clean up inconsistent NAP data across key UK directories and target reputable regional sources such as South Wales business registers, local chamber directories, and industry-specific portals to build high authority local trust signals."
}, {
  id: "faq-9",
  question: "How do you measure and report Local SEO leads, phone calls, and ROI?",
  answer: "I set up transparent call tracking, local Map Pack ranking reports, Google Business Profile insight tracking, and organic conversion funnels in Google Analytics 4. You receive clear, human-written monthly reports showing actual lead growth, phone enquiries, and traffic quality."
}, {
  id: "faq-10",
  question: "What industries in Swansea do you specialize in for local search?",
  answer: "I work with a range of B2B and consumer businesses across Swansea, including legal practices, dental & medical clinics, trade services, property agencies, hospitality venues, engineering firms, and local e-commerce stores seeking regional dominance."
}, {
  id: "faq-11",
  question: "Do I need to sign a rigid long-term contract for Swansea Local SEO?",
  answer: "No. I believe in earning your business through measurable results. I offer flexible monthly agreements with transparent deliverables, clear roadmaps, and full ownership of all assets created during our consultancy."
}, {
  id: "faq-12",
  question: "How do we get started with a Swansea Local SEO audit?",
  answer: "Simply book a free 30-minute SEO consultation directly with me. I will evaluate your current Swansea visibility, review your Google Business Profile, identify quick-win opportunities, and deliver a tailored action plan with zero sales pressure."
}];
export default function Localseofaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseofaqswansea" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {}
      <div className="absolute top-0 right-0 -z-10 size-96 rounded-full bg-secondary/40 blur-3xl opacity-60 pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 size-96 rounded-full bg-accent/10 blur-3xl opacity-50 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground rounded-full">
            <MapPin className="size-3.5 text-primary" />
            <span>Swansea Local Search Guide</span>
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold font-serif tracking-tight text-foreground mb-6">
            Frequently Asked Questions About <span className="text-primary underline decoration-accent/40 underline-offset-8">Local SEO in Swansea</span>
          </h2>
          
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            I work directly with Swansea and South Wales business owners to establish local search authority and drive qualified inbound enquiries. Here are straightforward answers to common questions about my local SEO consulting.
          </p>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="bg-card border border-border rounded-2xl p-6 sm:p-8 lg:p-10 shadow-md mb-16">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, i) => <motion.div key={faq.id} variants={itemVariants} data-index={i}>
                <AccordionItem value={faq.id} className="border border-border/80 rounded-xl px-4 sm:px-6 py-1 bg-background hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                  <AccordionTrigger className="text-base sm:text-lg font-semibold text-primary hover:text-primary/90 hover:no-underline py-5 text-left gap-4">
                    <span className="flex items-start gap-3">
                      <HelpCircle className="size-5 text-accent shrink-0 mt-0.5" />
                      <span>{faq.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-6 pt-1 pl-8 sm:pl-8 border-t border-border/40 mt-1">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>)}
          </Accordion>
        </motion.div>

        {}
        <div className="bg-muted/60 border border-border rounded-2xl p-8 sm:p-10 lg:p-12 text-center relative overflow-hidden shadow-sm">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/10 text-primary mb-2">
              <MessageSquare className="size-6" />
            </div>

            <h3 className="text-2xl sm:text-3xl font-semibold font-serif text-foreground">
              Have a Specific Question About Your Swansea Market Position?
            </h3>

            <p className="text-muted-foreground text-base sm:text-lg">
              Every local market has unique competitive nuances. Connect with me directly for an honest, actionable review of your website and Google Map Pack status.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto rounded-lg px-8 py-6 text-base font-medium shadow-md">
                <Link to="/ContactPage">
                  <span>Book Free Swansea SEO Audit</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-lg px-8 py-6 text-base font-medium border-border">
                <Link to="/LocalSEOPage">
                  <span>Explore Local SEO Services</span>
                </Link>
              </Button>
            </div>

            <div className="pt-4 flex items-center justify-center gap-6 text-xs sm:text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary" />
                <span>Direct Consultant Access</span>
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary" />
                <span>No Agency Account Managers</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}